/**
 * Gestor de compartir archivos PDF
 */
class ShareManager {
    constructor() {
        this.currentPdfBlob = null;
        this.currentPdfUrl = null;
    }

    /**
     * Establecer PDF actual para compartir
     */
    setPDF(pdfBlob) {
        if (!pdfBlob) {
            console.error('❌ ShareManager: No se proporcionó blob PDF');
            return false;
        }

        console.log('📤 ShareManager: Estableciendo PDF para compartir...');
        
        // Limpiar URL anterior si existe
        if (this.currentPdfUrl) {
            URL.revokeObjectURL(this.currentPdfUrl);
        }
        
        this.currentPdfBlob = pdfBlob;
        this.currentPdfUrl = URL.createObjectURL(pdfBlob);
        
        console.log('✅ ShareManager: PDF establecido correctamente');
        console.log(`📊 Tamaño: ${Math.round(pdfBlob.size / 1024)} KB`);
        
        return true;
    }

    /**
     * Verificar si tiene PDF
     */
    hasPDF() {
        return !!(this.currentPdfBlob && this.currentPdfUrl);
    }

    /**
     * Generar texto para compartir según tipo de reporte
     */
    generateShareText(reportType = 'generico') {
        const currentDate = new Date().toLocaleString();
        const pdfSizeKB = this.currentPdfBlob ? Math.round(this.currentPdfBlob.size / 1024) : 0;
        
        const shareTexts = {
            'recorridos': `🚗 *Reporte de Recorridos - Quro GPS*\n\n` +
                         `📅 Generado: ${currentDate}\n` +
                         `📊 Tamaño: ${pdfSizeKB} KB\n` +
                         `🗺️ Incluye: Mapa, velocidades, kilometraje y tabla detallada\n` +
                         `📈 Análisis completo de rutas y estadías\n\n` +
                         `🔗 Sistema Quro GPS\n` +
                         `📎 Archivo PDF disponible para descarga`,
            
            'alertas': `🚨 *Reporte de Alertas - Quro GPS*\n\n` +
                      `📅 Generado: ${currentDate}\n` +
                      `📊 Tamaño: ${pdfSizeKB} KB\n` +
                      `🔔 Incluye: Análisis de alertas, gráficos y tabla detallada\n` +
                      `📈 Monitoreo completo de eventos\n\n` +
                      `🔗 Sistema Quro GPS\n` +
                      `📎 Archivo PDF disponible para descarga`,
            
            'combustible': `⛽ *Reporte de Combustible - Quro GPS*\n\n` +
                          `📅 Generado: ${currentDate}\n` +
                          `📊 Tamaño: ${pdfSizeKB} KB\n` +
                          `📈 Incluye: Análisis de consumo, niveles y tendencias\n` +
                          `⚡ Control completo de combustible\n\n` +
                          `🔗 Sistema Quro GPS\n` +
                          `📎 Archivo PDF disponible para descarga`,
            
            'generico': `📋 *Reporte del Sistema - Quro GPS*\n\n` +
                       `📅 Generado: ${currentDate}\n` +
                       `📊 Tamaño: ${pdfSizeKB} KB\n` +
                       `📈 Incluye: Gráficos, tablas y análisis completo\n\n` +
                       `🔗 Sistema Quro GPS\n` +
                       `📎 Archivo PDF disponible para descarga`
        };
        
        return shareTexts[reportType] || shareTexts['generico'];
    }

    /**
     * Intentar compartir con Web Share API nativo
     */
    async tryNativeShare(fileName, title, shareText) {
        if (!this.hasPDF()) {
            console.error('❌ ShareManager: No hay PDF para compartir');
            return false;
        }

        if (navigator.share && navigator.canShare) {
            try {
                console.log('📱 Intentando compartir con Web Share API...');
                
                const file = new File([this.currentPdfBlob], fileName, { type: 'application/pdf' });
                
                const shareData = {
                    title,
                    text: shareText,
                    files: [file]
                };
                
                if (navigator.canShare(shareData)) {
                    await navigator.share(shareData);
                    console.log('✅ Compartido con Web Share API');
                    return true;
                } else {
                    console.log('⚠️ Web Share API no puede compartir este contenido');
                }
            } catch (error) {
                console.warn('⚠️ Error con Web Share API:', error);
            }
        } else {
            console.log('⚠️ Web Share API no disponible');
        }
        return false;
    }

    /**
     * Compartir por WhatsApp
     */
    shareWhatsApp(shareText) {
        console.log('📱 Compartiendo por WhatsApp...');
        const whatsappText = encodeURIComponent(shareText);
        window.open(`https://wa.me/?text=${whatsappText}`, '_blank');
        return true;
    }

    /**
     * Compartir por Email
     */
    shareEmail(fileName, shareText) {
        console.log('📧 Compartiendo por Email...');
        const subject = encodeURIComponent('Reporte - Quro GPS');
        const body = encodeURIComponent(
            `${shareText}\n\n📎 NOTA: Descargue el PDF y adjúntelo manualmente al email.\n` +
            `Archivo: ${fileName}\n\nSaludos,\nSistema Quro GPS`
        );
        window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
        return true;
    }

    /**
     * Copiar texto al portapapeles
     */
    async copyToClipboard(shareText) {
        try {
            console.log('📋 Copiando al portapapeles...');
            
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(shareText);
                console.log('✅ Texto copiado al portapapeles');
                return true;
            } else {
                // Fallback para navegadores más antiguos
                console.log('🔄 Usando fallback para copiar...');
                const textArea = document.createElement('textarea');
                textArea.value = shareText;
                textArea.style.position = 'fixed';
                textArea.style.opacity = '0';
                textArea.style.top = '-9999px';
                textArea.style.left = '-9999px';
                
                document.body.appendChild(textArea);
                textArea.focus();
                textArea.select();
                
                const success = document.execCommand('copy');
                document.body.removeChild(textArea);
                
                if (success) {
                    console.log('✅ Texto copiado con fallback');
                    return true;
                } else {
                    throw new Error('execCommand failed');
                }
            }
        } catch (error) {
            console.error('❌ Error copiando al portapapeles:', error);
            return false;
        }
    }

    /**
     * Descargar PDF
     */
    downloadPDF(fileName = null) {
        if (!this.hasPDF()) {
            console.error('❌ ShareManager: No hay PDF para descargar');
            return false;
        }

        try {
            console.log('📥 Iniciando descarga de PDF...');
            
            const finalFileName = fileName || `reporte-quro-${new Date().getTime()}.pdf`;
            const link = document.createElement('a');
            
            link.download = finalFileName;
            link.href = this.currentPdfUrl;
            link.style.display = 'none';
            
            // Agregar al DOM temporalmente
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            console.log(`✅ PDF descargado: ${finalFileName}`);
            return true;
        } catch (error) {
            console.error('❌ Error descargando PDF:', error);
            return false;
        }
    }

    /**
     * Imprimir PDF
     */
    printPDF() {
        if (!this.hasPDF()) {
            console.error('❌ ShareManager: No hay PDF para imprimir');
            return false;
        }

        try {
            console.log('🖨️ Abriendo PDF para imprimir...');
            
            const printWindow = window.open(this.currentPdfUrl, '_blank');
            
            if (printWindow) {
                printWindow.onload = function() {
                    // Dar tiempo para que cargue completamente
                    setTimeout(() => {
                        printWindow.print();
                    }, 1000);
                };
                
                console.log('✅ Ventana de impresión abierta');
                return true;
            } else {
                throw new Error('No se pudo abrir ventana de impresión');
            }
        } catch (error) {
            console.error('❌ Error imprimiendo PDF:', error);
            return false;
        }
    }

    /**
     * Abrir PDF en nueva ventana
     */
    openInNewWindow() {
        if (!this.hasPDF()) {
            console.error('❌ ShareManager: No hay PDF para abrir');
            return false;
        }

        try {
            console.log('🔗 Abriendo PDF en nueva ventana...');
            const newWindow = window.open(this.currentPdfUrl, '_blank');
            
            if (newWindow) {
                console.log('✅ PDF abierto en nueva ventana');
                return true;
            } else {
                throw new Error('No se pudo abrir nueva ventana');
            }
        } catch (error) {
            console.error('❌ Error abriendo PDF:', error);
            return false;
        }
    }

    /**
     * Generar nombre de archivo según tipo de reporte
     */
    generateFileName(reportType = 'generico') {
        const timestamp = new Date().getTime();
        const date = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
        
        const prefixes = {
            'recorridos': 'reporte-recorridos',
            'alertas': 'reporte-alertas',
            'combustible': 'reporte-combustible',
            'velocidad': 'reporte-velocidad',
            'ubicaciones': 'reporte-ubicaciones',
            'generico': 'reporte-quro'
        };
        
        const prefix = prefixes[reportType] || prefixes['generico'];
        return `${prefix}-${date}-${timestamp}.pdf`;
    }

    /**
     * Debug: Estado del ShareManager
     */
    getDebugInfo() {
        return {
            hasPdfBlob: !!this.currentPdfBlob,
            hasPdfUrl: !!this.currentPdfUrl,
            pdfSize: this.currentPdfBlob ? this.currentPdfBlob.size : 0,
            pdfSizeKB: this.currentPdfBlob ? Math.round(this.currentPdfBlob.size / 1024) : 0,
            webShareAvailable: !!(navigator.share && navigator.canShare),
            clipboardAvailable: !!navigator.clipboard
        };
    }

    async sharePDF(fileName = null, reportType = 'generico') {
        if (!this.hasPDF()) {
            console.error('❌ ShareManager: No hay PDF para compartir');
            throw new Error('No hay PDF disponible para compartir');
        }

        try {
            console.log('📤 ShareManager: Iniciando proceso de compartir...');
            
            // ✅ GENERAR NOMBRE Y TEXTOS
            const finalFileName = fileName || this.generateFileName(reportType);
            const shareText = this.generateShareText(reportType);
            const title = `Reporte - Quro GPS`;
            
            console.log(`📄 Archivo: ${finalFileName}`);
            console.log(`📊 Tamaño: ${this.getDebugInfo().pdfSizeKB} KB`);
            
            // ✅ INTENTAR COMPARTIR NATIVO PRIMERO
            const nativeShareSuccess = await this.tryNativeShare(finalFileName, title, shareText);
            
            if (nativeShareSuccess) {
                console.log('✅ Compartido exitosamente con API nativo');
                return {
                    success: true,
                    method: 'native',
                    fileName: finalFileName
                };
            }
            
            // ✅ SI NO FUNCIONA NATIVO, MOSTRAR OPCIONES MANUALES
            console.log('📱 API nativo no disponible, mostrando opciones...');
            
            // Aquí podrías mostrar un modal con opciones o ejecutar una acción por defecto
            // Por ahora, intentaremos descargar como fallback
            const downloadSuccess = this.downloadPDF(finalFileName);
            
            if (downloadSuccess) {
                console.log('✅ PDF descargado como alternativa');
                return {
                    success: true,
                    method: 'download',
                    fileName: finalFileName,
                    message: 'PDF descargado. Puedes compartirlo manualmente.'
                };
            }
            
            throw new Error('No se pudo compartir ni descargar el PDF');
            
        } catch (error) {
            console.error('❌ Error en sharePDF():', error);
            return {
                success: false,
                error: error.message,
                method: 'none'
            };
        }
    }

    /**
     * ✅ MÉTODO ALTERNATIVO: Mostrar opciones de compartir
     * Para cuando no hay API nativo disponible
     */
    async showShareOptions(fileName = null, reportType = 'generico') {
        if (!this.hasPDF()) {
            console.error('❌ ShareManager: No hay PDF para compartir');
            return false;
        }

        try {
            const finalFileName = fileName || this.generateFileName(reportType);
            const shareText = this.generateShareText(reportType);
            
            console.log('📋 Mostrando opciones de compartir...');
            
            // ✅ ESTO PODRÍA LLAMAR AL MODAL MANAGER
            // Por ahora, log de las opciones disponibles
            console.log('📤 Opciones disponibles:');
            console.log('   📥 Descargar PDF');
            console.log('   🖨️ Imprimir PDF');
            console.log('   📱 WhatsApp (solo texto)');
            console.log('   📧 Email (solo texto)');
            console.log('   📋 Copiar texto');
            
            // Como fallback, descargar el PDF
            return this.downloadPDF(finalFileName);
            
        } catch (error) {
            console.error('❌ Error mostrando opciones:', error);
            return false;
        }
    }

    /**
     * ✅ MÉTODO PARA COMPARTIR SOLO TEXTO (sin PDF)
     */
    async shareTextOnly(reportType = 'generico') {
        try {
            const shareText = this.generateShareText(reportType);
            
            console.log('📝 Compartiendo solo texto...');
            
            // Intentar copiar al portapapeles
            const copySuccess = await this.copyToClipboard(shareText);
            
            if (copySuccess) {
                return {
                    success: true,
                    method: 'clipboard',
                    message: 'Texto copiado al portapapeles'
                };
            }
            
            throw new Error('No se pudo copiar el texto');
            
        } catch (error) {
            console.error('❌ Error compartiendo texto:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    /**
     * Limpiar recursos
     */
    cleanup() {
        console.log('🧹 ShareManager: Limpiando recursos...');
        
        if (this.currentPdfUrl) {
            URL.revokeObjectURL(this.currentPdfUrl);
            this.currentPdfUrl = null;
        }
        
        this.currentPdfBlob = null;
        console.log('✅ ShareManager: Recursos limpiados');
    }
}

export default ShareManager;